/**
 * Product image viewer (Alpine component).
 *
 * Two levels of inspection, progressively enhanced:
 *   - Desktop main viewer: cursor-follow hover zoom to inspect detail in place.
 *   - Click anywhere on the image: a full-screen lightbox with click-to-zoom,
 *     drag-to-pan, thumbnail navigation, prev/next, counter, and keyboard.
 *
 * Honors prefers-reduced-motion (no hover zoom, transitions are instant) and
 * keeps working with a single image (zoom only, nav/thumbs hidden). Requires
 * no image data beyond the list of URLs the Blade passes in.
 */
export function registerProductGallery(Alpine) {
    Alpine.data('productGallery', (config = {}) => ({
        images: Array.isArray(config.images) ? config.images.filter(Boolean) : [],
        active: config.initial || (Array.isArray(config.images) ? config.images[0] : '') || '',
        reduce: false,

        // Inline hover zoom (main viewer)
        hover: false,
        ox: 50,
        oy: 50,

        // Lightbox
        open: false,
        zoomed: false,
        px: 0,
        py: 0,

        init() {
            this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (!this.images.length && this.active) this.images = [this.active];
            // Variant picker can swap the main image.
            window.addEventListener('variant-changed', (e) => {
                if (e.detail && e.detail.image) this.select(e.detail.image);
            });
        },

        get index() {
            const i = this.images.indexOf(this.active);
            return i < 0 ? 0 : i;
        },
        get multi() {
            return this.images.length > 1;
        },

        // ── Main viewer hover zoom ───────────────────────────────────────────
        move(e) {
            if (this.reduce) return;
            const r = e.currentTarget.getBoundingClientRect();
            this.ox = ((e.clientX - r.left) / r.width) * 100;
            this.oy = ((e.clientY - r.top) / r.height) * 100;
        },
        zoomStyle() {
            if (!this.hover || this.reduce) return { transform: 'none', transformOrigin: 'center center' };
            return { transform: 'scale(1.9)', transformOrigin: `${this.ox}% ${this.oy}%` };
        },

        // ── Lightbox ─────────────────────────────────────────────────────────
        openLb() {
            this.open = true;
            this.resetZoom();
            window.scrollLock?.lock('gallery-lightbox');
        },
        closeLb() {
            this.open = false;
            window.scrollLock?.unlock('gallery-lightbox');
        },
        select(img) {
            this.active = img;
            this.resetZoom();
        },
        next() {
            if (this.multi) this.select(this.images[(this.index + 1) % this.images.length]);
        },
        prev() {
            if (this.multi) this.select(this.images[(this.index - 1 + this.images.length) % this.images.length]);
        },
        resetZoom() {
            this.zoomed = false;
            this.px = 0;
            this.py = 0;
        },
        toggleZoom() {
            this.zoomed = !this.zoomed;
            if (!this.zoomed) {
                this.px = 0;
                this.py = 0;
            }
        },
        pan(e) {
            if (!this.zoomed) return;
            const r = e.currentTarget.getBoundingClientRect();
            this.px = -(((e.clientX - r.left) / r.width) - 0.5) * 50;
            this.py = -(((e.clientY - r.top) / r.height) - 0.5) * 50;
        },
        lbStyle() {
            const s = this.zoomed ? 2.2 : 1;
            return { transform: `scale(${s}) translate(${this.px}%, ${this.py}%)` };
        },
    }));
}
